import { useEffect } from 'react';
import type { User } from 'firebase/auth';

interface StripeHandlersDeps {
  user: User | null;
  subscription: { customerId?: string | null };
  setDevMode: (devMode: boolean) => void;
  modals: {
    openPremiumSuccessModal: () => void;
  };
}

export function useStripeHandlers(deps: StripeHandlersDeps) {
  const { user, subscription, setDevMode, modals } = deps;

  // Handle Stripe checkout success return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');

    if (sessionId || success === 'true') {
      console.log('Stripe checkout completed, session:', sessionId);
      window.history.replaceState({}, document.title, window.location.pathname);

      const verifyCheckout = async () => {
        try {
          if (sessionId) {
            const apiUrl = import.meta.env.DEV ? 'http://localhost:3001' : '';
            const response = await fetch(`${apiUrl}/api/stripe/verify-session?session_id=${sessionId}`);

            if (response.ok) {
              const data = await response.json();
              console.log('Checkout verified:', data);

              if (data.status === 'complete' && data.payment_status === 'paid') {
                if (typeof window !== 'undefined' && (window as any).dataLayer) {
                  (window as any).dataLayer.push({
                    'event': 'premium_subscribe',
                    'user_id': user?.uid || 'unknown',
                    'subscription_value': 1.20
                  });
                  console.log('GTM Event: premium_subscribe -', user?.uid);
                }

                localStorage.setItem('DEV_FORCE_PREMIUM', 'true');
                setDevMode(true);
                modals.openPremiumSuccessModal();
                return;
              }
            }
          }

          console.warn('Could not verify session, setting premium anyway for test mode');

          if (typeof window !== 'undefined' && (window as any).dataLayer) {
            (window as any).dataLayer.push({
              'event': 'premium_subscribe',
              'user_id': user?.uid || 'unknown',
              'subscription_value': 1.20
            });
            console.log('GTM Event: premium_subscribe (fallback) -', user?.uid);
          }

          localStorage.setItem('DEV_FORCE_PREMIUM', 'true');
          setDevMode(true);
          modals.openPremiumSuccessModal();
        } catch (error) {
          console.error('Error verifying checkout:', error);

          if (typeof window !== 'undefined' && (window as any).dataLayer) {
            (window as any).dataLayer.push({
              'event': 'premium_subscribe',
              'user_id': user?.uid || 'unknown',
              'subscription_value': 1.20
            });
            console.log('GTM Event: premium_subscribe (error fallback) -', user?.uid);
          }

          localStorage.setItem('DEV_FORCE_PREMIUM', 'true');
          setDevMode(true);
          modals.openPremiumSuccessModal();
        }
      };

      verifyCheckout();
    }
  }, [setDevMode, modals]);

  const handleOpenStripePortal = async () => {
    if (!subscription.customerId || !user) {
      alert('Unable to access billing portal. Please try logging out and back in.');
      return;
    }

    try {
      const token = await user.getIdToken(true);
      const response = await fetch('/api/stripe/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ customerId: subscription.customerId })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create portal session');
      }

      const { url } = await response.json();
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error opening Stripe portal:', error);
      alert('Unable to open billing portal. Please try again.');
    }
  };

  return {
    handleOpenStripePortal,
  };
}

export type UseStripeHandlersReturn = ReturnType<typeof useStripeHandlers>;
